import React, { useState, useEffect, useRef } from 'react';
import { m } from 'framer-motion';
import { FiPlus, FiMinus, FiGift, FiLoader } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from 'react-i18next';
import { useMotionConfig } from '../../hooks/useReducedMotion';
import { STRIPE_CONFIG } from '../../config/stripe';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

function durationToHours(type, value) {
  const v = Number(value) || 0;
  if (type === 'hours') return v;
  if (type === 'calendar_day' || type === 'multi_day' || type === 'days') return v * 24;
  if (type === 'week' || type === 'weeks') return v * 168;
  return v;
}

function findBestPricingRule(pricingRules, startDate, endDate) {
  if (!pricingRules || pricingRules.length === 0) return null;
  const durationHours = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60);

  const sorted = pricingRules
    .map(r => ({ ...r, _hours: durationToHours(r.duration_type, r.duration_value) }))
    .sort((a, b) => a._hours - b._hours);

  return sorted.find(r => r._hours >= durationHours) || sorted[sorted.length - 1];
}

function hasDepositRule(item, depositRules) {
  if (!depositRules || depositRules.length === 0) return false;
  const slug = item.category_slug || item.category || '';
  return depositRules.some(r => r.category_slug === slug || r.category_slug === '*');
}

export default function AddExtrasSection({
  booking,
  locationId,
  startDate,
  endDate,
  existingAddonIds,
  newAddons,
  onAddAddon,
  onRemoveAddon,
  depositRules
}) {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { getProductTranslation } = useLanguage();
  const { t } = useTranslation();
  const motion = useMotionConfig();

  const [availableAddons, setAvailableAddons] = useState([]);
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!expanded || fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAddonsAndAvailability = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/addons?locationId=${locationId}`);
        if (!res.ok) return;
        const data = await res.json();

        const allAddons = (data.addons || []).filter(a => {
          const hasPrice = a.pricing_rules && a.pricing_rules.length > 0;
          const notAlreadyInBooking = !existingAddonIds.includes(a.id);
          return hasPrice && notAlreadyInBooking;
        });

        if (!startDate || !endDate || allAddons.length === 0) {
          const filtered = depositRules
            ? allAddons.filter(a => !hasDepositRule(a, depositRules))
            : allAddons;
          setAvailableAddons(filtered.map(a => {
            const rule = findBestPricingRule(a.pricing_rules, startDate, endDate);
            return { ...a, price: rule?.price || a.pricing_rules[0]?.price || 0 };
          }));
          setLoading(false);
          return;
        }

        const start = new Date(startDate).toISOString();
        const end = new Date(endDate).toISOString();

        const availMap = {};
        const params = new URLSearchParams({ locationId, startDate: start, endDate: end });
        const availRes = await fetch(`${API_BASE}/availability?${params}`);
        if (availRes.ok) {
          const availData = await availRes.json();
          (availData.availability || []).forEach(entry => {
            const key = `${entry.product_type_id}_${entry.size || 'null'}`;
            availMap[key] = entry.available_count || 0;
          });
        }
        setAvailabilityMap(availMap);

        const withAvailability = allAddons
          .filter(a => {
            const key = `${a.id}_null`;
            const count = availMap[key] || availMap[`${a.id}_`] || 0;
            return a.inventory_type === 'unlimited' || count > 0;
          })
          .map(a => {
            const rule = findBestPricingRule(a.pricing_rules, startDate, endDate);
            return { ...a, price: rule?.price || a.pricing_rules[0]?.price || 0 };
          });

        const withoutDeposit = depositRules
          ? withAvailability.filter(a => !hasDepositRule(a, depositRules))
          : withAvailability;

        setAvailableAddons(withoutDeposit);
      } catch {
        setAvailableAddons([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAddonsAndAvailability();
  }, [expanded, locationId, startDate, endDate]);

  useEffect(() => {
    fetchedRef.current = false;
  }, [startDate, endDate]);

  const getAddedQty = (addonId) => {
    return (newAddons || [])
      .filter(a => a.id === addonId)
      .reduce((sum, a) => sum + a.quantity, 0);
  };

  const getAvailCount = (addon) => {
    if (addon.inventory_type === 'unlimited') return 999;
    const key = `${addon.id}_null`;
    const total = availabilityMap[key] || availabilityMap[`${addon.id}_`] || 0;
    return Math.max(0, total - getAddedQty(addon.id));
  };

  if (!expanded) {
    return (
      <m.div {...motion.fadeInUp}>
        <button
          onClick={() => setExpanded(true)}
          className="w-full bg-white rounded-2xl shadow-sm border border-dashed border-gray-300 p-5 flex items-center justify-center gap-2 text-sm font-medium hover:border-gray-400 hover:bg-gray-50 transition-colors"
          style={{ color: theme.colors.primary.base }}
        >
          <SafeIcon icon={FiGift} className="w-5 h-5" />
          {t('manage.addExtras', 'Add extras')}
        </button>
      </m.div>
    );
  }

  return (
    <m.div {...motion.fadeInUp} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <SafeIcon icon={FiGift} className="w-5 h-5" style={{ color: theme.colors.primary.base }} />
        {t('manage.addExtras', 'Add extras')}
      </h2>

      {loading ? (
        <div role="status" aria-live="polite" className="flex items-center justify-center py-8 gap-2 text-gray-600">
          <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
          <span className="text-sm">{t('manage.loadingExtras', 'Loading extras...')}</span>
        </div>
      ) : availableAddons.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          {t('manage.noAdditionalExtras', 'No additional extras available.')}
        </p>
      ) : (
        <div className="space-y-3">
          {availableAddons.map((addon) => {
            const addonName = getProductTranslation(
              addon.product_name, addon.translations, 'product_name'
            );
            const qty = getAddedQty(addon.id);
            const avail = getAvailCount(addon);
            const canAdd = avail > 0;

            return (
              <div key={addon.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                {addon.picture_url && (
                  <img
                    src={addon.picture_url}
                    alt={addonName}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm truncate">{addonName}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {formatCurrency(addon.price)}/{t('manage.each', 'ea')}
                    </span>
                    {addon.inventory_type !== 'unlimited' && (
                      <span className={`text-xs font-medium ${avail > 0 || qty > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {avail > 0
                          ? t('manage.availableCount', '{{count}} available', { count: avail })
                          : qty > 0
                            ? t('manage.maximumSelected', 'Maximum selected')
                            : t('manage.soldOut', 'Sold out')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {qty > 0 && (
                    <button
                      onClick={() => onRemoveAddon(addon.id)}
                      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    >
                      <SafeIcon icon={FiMinus} className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                  <span className="w-8 text-center font-semibold text-gray-900">{qty}</span>
                  <button
                    onClick={() => onAddAddon(addon)}
                    disabled={!canAdd}
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors text-white disabled:opacity-30"
                    style={{ backgroundColor: theme.colors.primary.base }}
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </m.div>
  );
}
